<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import ActivityFeed, { type ActivityItem } from '../../components/ActivityFeed.svelte';
    import ConfirmModal from '../../components/ConfirmModal.svelte';
    import MediaLightbox from '../../components/MediaLightbox.svelte';
    import Modal from '../../components/Modal.svelte';
    import StatCard from '../../components/StatCard.svelte';
    import { useForm, page } from '@inertiajs/svelte';
    import { SvelteSet } from 'svelte/reactivity';
    import { get } from 'svelte/store';
    import { tick } from 'svelte';
    import { slide } from 'svelte/transition';
    import { timeAgo } from '../../utils/dates';
    import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

    Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

    interface ScheduleDetail {
        id: number;
        date: string;
        date_formatted: string;
        computed_status: string;
    }

    interface AnswerStat {
        schedule_id: number;
        date: string;
        date_formatted: string;
        total: number;
        correct: number;
        incorrect: number;
        pending: number;
        percent_correct: number;
    }

    interface TimeSeriesBucket {
        hour: number;
        correct: number;
        incorrect: number;
        pending: number;
    }

    interface AiAnswer {
        model_name: string;
        question_body: string;
        response_text: string | null;
        selected_option_body: string | null;
        is_correct: boolean | null;
        reasoning: string | null;
        provider: string | null;
        model: string | null;
        schedule_date: string;
        submitted_at: string | null;
    }

    interface WorkflowEvent {
        type: string;
        label: string;
        detail: string | null;
        actor: string | null;
        timestamp: string;
    }

    let {
        case: c,
        activity = [],
        workflow_events: workflowEvents = [],
        schedule_details: scheduleDetails = [],
        answer_stats: answerStats = [],
        answer_time_series: answerTimeSeries = {},
        ai_answers: aiAnswers = [],
    }: {
        case: any;
        activity?: ActivityItem[];
        workflow_events?: WorkflowEvent[];
        schedule_details?: ScheduleDetail[];
        answer_stats?: AnswerStat[];
        answer_time_series?: Record<number, TimeSeriesBucket[]>;
        ai_answers?: AiAnswer[];
    } = $props();

    let approveModalOpen = $state(false);

    function approve() {
        approveModalOpen = false;
        const form = useForm({});
        get(form).post(`/admin/cases/${c.id}/approve`, {
            invalidateCacheTags: ['admin-cases'],
        });
    }

    let declineModalOpen = $state(false);

    function decline() {
        declineModalOpen = false;
        const form = useForm({});
        get(form).post(`/admin/cases/${c.id}/decline`, {
            invalidateCacheTags: ['admin-cases'],
        });
    }

    let revisionModalOpen = $state(false);
    let revisionFeedback = $state('');

    function requestRevision() {
        revisionModalOpen = false;
        const form = useForm({ feedback: revisionFeedback });
        get(form).post(`/admin/cases/${c.id}/request-revision`, {
            invalidateCacheTags: ['admin-cases'],
        });
    }

    // Images
    const lightboxImages = $derived(
        (c.media_urls ?? []).map((m: any) => ({ src: m.url, alt: m.caption || 'Case image', caption: m.caption || '' }))
    );
    let lightboxOpen = $state(false);
    let lightboxIndex = $state(0);
    let lightboxRef: MediaLightbox;

    function openLightbox(index: number, e: MouseEvent) {
        const img = (e.currentTarget as HTMLElement).querySelector('img') as HTMLElement;
        lightboxRef.openFrom(index, img);
    }

    const additionalLightboxImages = $derived(
        (c.additional_media_urls ?? []).map((m: any) => ({ src: m.url, alt: m.caption || 'Additional image', caption: m.caption || '' }))
    );
    let additionalLightboxOpen = $state(false);
    let additionalLightboxIndex = $state(0);
    let additionalLightboxRef: MediaLightbox;

    function openAdditionalLightbox(index: number, e: MouseEvent) {
        const img = (e.currentTarget as HTMLElement).querySelector('img') as HTMLElement;
        additionalLightboxRef.openFrom(index, img);
    }

    // Case status badge colors
    const caseStatusClasses: Record<string, string> = {
        draft: 'bg-gray-800 text-gray-400',
        submitted: 'bg-amber-900 text-amber-300',
        approved: 'bg-emerald-900 text-emerald-300',
        needs_revision: 'bg-violet-900 text-violet-300',
        declined: 'bg-rose-900 text-rose-300',
    };

    // Workflow event styling
    const workflowEventConfig: Record<string, { dot: string; icon: string }> = {
        created:            { dot: 'bg-gray-600',    icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 512a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM232 344l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0 13.3-10.7 24-24 24s-24-10.7-24-24z"/></svg>' },
        submitted:          { dot: 'bg-amber-600',   icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M536.4-26.3c9.8-3.5 20.6-1 28 6.3s9.8 18.2 6.3 28l-178 496.9c-5 13.9-18.1 23.1-32.8 23.1-14.2 0-27-8.6-32.3-21.7l-64.2-158c-4.5-11-2.5-23.6 5.2-32.6l94.5-112.4c5.1-6.1 4.7-15-.9-20.6s-14.6-6-20.6-.9L229.2 276.1c-9.1 7.6-21.6 9.6-32.6 5.2L38.1 216.8c-13.1-5.3-21.7-18.1-21.7-32.3 0-14.7 9.2-27.8 23.1-32.8l496.9-178z"/></svg>' },
        approved:           { dot: 'bg-emerald-600', icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 512a256 256 0 1 1 0-512 256 256 0 1 1 0 512zM374 145.7c-10.7-7.8-25.7-5.4-33.5 5.3L221.1 315.2 169 263.1c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l72 72c5 5 11.8 7.5 18.8 7s13.4-4.1 17.5-9.8L379.3 179.2c7.8-10.7 5.4-25.7-5.3-33.5z"/></svg>' },
        declined:           { dot: 'bg-rose-600',    icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M133.1 32c17.3 0 34.2 3.4 49.9 9.7l54.8 85.2-75.4 75.4c-1.5 1.5-2.4 3.6-2.3 5.8s1 4.2 2.6 5.7l112 104c2.9 2.7 7.4 2.9 10.5 .3s3.8-7 1.7-10.4l-60.4-98.1 90.7-75.6c2.6-2.1 3.5-5.7 2.4-8.8L296.4 60.6C319.6 42.3 348.6 32 378.9 32 452.4 32 512 91.6 512 165.1l0 2.6c0 112.2-139.9 242.5-212.9 298.2-12.4 9.4-27.6 14.1-43.1 14.1s-30.8-4.6-43.1-14.1C139.9 410.2 0 279.9 0 167.7l0-2.6C0 91.6 59.6 32 133.1 32z"/></svg>' },
        needs_revision:     { dot: 'bg-violet-600',  icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L368 46.1 465.9 144 490.3 119.6c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L432 177.9 334.1 80 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z"/></svg>' },
        revision_requested: { dot: 'bg-violet-600',  icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L368 46.1 465.9 144 490.3 119.6c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L432 177.9 334.1 80 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z"/></svg>' },
        draft:              { dot: 'bg-gray-600',    icon: '<svg class="size-3 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M0 256a256 256 0 1 1 512 0 256 256 0 1 1 -512 0z"/></svg>' },
    };

    function getWorkflowConfig(type: string) {
        return workflowEventConfig[type] ?? workflowEventConfig.created;
    }

    // Schedule status colors
    function statusColor(status: string): string {
        switch (status) {
            case 'open': return 'bg-cyan-900 text-cyan-300';
            case 'revealed': return 'bg-emerald-900 text-emerald-300';
            case 'scheduled': return 'bg-amber-900 text-amber-300';
            case 'closed': return 'bg-gray-700 text-gray-300';
            default: return 'bg-gray-700 text-gray-400';
        }
    }

    // Answer chart
    let chartCanvas: HTMLCanvasElement;
    let chartInstance: Chart | null = null;
    let selectedStatScheduleId = $state<number | null>(null);

    const selectedStat = $derived(
        answerStats.length === 1
            ? answerStats[0]
            : answerStats.find(s => s.schedule_id === selectedStatScheduleId) ?? answerStats[0] ?? null
    );

    const selectedTimeSeries = $derived(
        selectedStat ? (answerTimeSeries[selectedStat.schedule_id] ?? []) : []
    );

    function formatHour(h: number): string {
        if (h === 0) return '12 AM';
        if (h < 12) return `${h} AM`;
        if (h === 12) return '12 PM';
        return `${h - 12} PM`;
    }

    $effect(() => {
        if (!statsOpen || selectedTimeSeries.length === 0) {
            if (chartInstance) { chartInstance.destroy(); chartInstance = null; }
            return;
        }

        tick().then(() => {
            if (!chartCanvas) return;

            const buckets = selectedTimeSeries;
            const labels = buckets.map((b: TimeSeriesBucket) => formatHour(b.hour));

            if (chartInstance) { chartInstance.destroy(); }

            // Create crosshatch pattern for pending bars
            const patternCanvas = document.createElement('canvas');
            patternCanvas.width = 8;
            patternCanvas.height = 8;
            const pctx = patternCanvas.getContext('2d')!;
            pctx.strokeStyle = '#6b7280';
            pctx.lineWidth = 1.5;
            pctx.beginPath();
            pctx.moveTo(0, 8);
            pctx.lineTo(8, 0);
            pctx.moveTo(-2, 2);
            pctx.lineTo(2, -2);
            pctx.moveTo(6, 10);
            pctx.lineTo(10, 6);
            pctx.stroke();
            const pendingPattern = chartCanvas.getContext('2d')!.createPattern(patternCanvas, 'repeat')!;

            chartInstance = new Chart(chartCanvas, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Correct',
                            data: buckets.map((b: TimeSeriesBucket) => b.correct),
                            backgroundColor: '#34d399',
                        },
                        {
                            label: 'Incorrect',
                            data: buckets.map((b: TimeSeriesBucket) => b.incorrect),
                            backgroundColor: '#fb7185',
                        },
                        {
                            label: 'Pending',
                            data: buckets.map((b: TimeSeriesBucket) => b.pending),
                            backgroundColor: pendingPattern,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            stacked: true,
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(75, 85, 99, 0.3)' },
                        },
                        y: {
                            stacked: true,
                            beginAtZero: true,
                            ticks: { color: '#9ca3af', precision: 0 },
                            grid: { color: 'rgba(75, 85, 99, 0.3)' },
                        },
                    },
                    plugins: {
                        legend: { labels: { color: '#d1d5db' } },
                        tooltip: { mode: 'index', intersect: false },
                    },
                },
            });
        });

        return () => {
            if (chartInstance) { chartInstance.destroy(); chartInstance = null; }
        };
    });

    // AI accuracy
    const aiCorrect = $derived(aiAnswers.filter((a: AiAnswer) => a.is_correct === true).length);
    const aiScored = $derived(aiAnswers.filter((a: AiAnswer) => a.is_correct !== null).length);
    const aiPercent = $derived(aiScored > 0 ? Math.round((aiCorrect / aiScored) * 100) : 0);

    // Collapsible sections
    let statsOpen = $state(false);
    let aiOpen = $state(false);

    // AI answers expand state
    let expandedAiRows = new SvelteSet<number>();

    function toggleAiRow(index: number) {
        if (expandedAiRows.has(index)) { expandedAiRows.delete(index); } else { expandedAiRows.add(index); }
    }
</script>

<svelte:head>
    <title>Case #{c.id} — Admin — Case of the Day</title>
</svelte:head>

<div class="max-w-5xl mx-auto flex gap-8">
<!-- Case Content -->
<div class="min-w-0 flex-1 space-y-8">
    <!-- Header -->
    <header>
        <div class="flex justify-between items-start">
            <div>
                {#if c.topic}
                    <p class="text-sm font-medium text-cyan-400 uppercase tracking-wide">{c.topic.name}</p>
                {/if}
                <h1 class="text-2xl font-bold text-gray-100 mt-1">Case #{c.id}</h1>
            </div>
            <span class="text-sm px-3 py-1 rounded-full capitalize {caseStatusClasses[c.status] ?? 'bg-gray-800 text-gray-400'}">{c.status.replace('_', ' ')}</span>
        </div>
        {#if c.submitted_by}
            <p class="text-sm text-gray-500 mt-3">Submitted by <span class="font-semibold text-gray-300">{c.submitted_by.name}</span> ({c.submitted_by.email})</p>
        {/if}
    </header>

    <!-- Scheduled Dates -->
    {#if scheduleDetails.length > 0}
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <h2 class="font-semibold mb-4">Scheduled Dates ({scheduleDetails.length})</h2>
            <div class="flex flex-wrap gap-2">
                {#each scheduleDetails as sd (sd.id)}
                    <span class="text-sm px-3 py-1 rounded-full capitalize {statusColor(sd.computed_status)}">
                        {sd.date_formatted}
                    </span>
                {/each}
            </div>
        </div>
    {/if}

    {#if c.status === 'declined'}
        <div class="flex items-center gap-3 rounded-lg border border-rose-500/30 bg-rose-950/40 px-5 py-4 text-rose-300">
            <svg class="size-5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path opacity=".4" d="M0 165.1l0 2.6c0 112.2 139.9 242.5 212.9 298.2 12.4 9.4 27.6 14.1 43.1 14.1s30.8-4.6 43.1-14.1c73-55.7 212.9-186 212.9-298.2l0-2.6c0-73.5-59.6-133.1-133.1-133.1-30.3 0-59.2 10.3-82.4 28.6l23.1 64.7c1.1 3.2 .2 6.7-2.4 8.8l-90.7 75.6 60.4 98.1c2.1 3.4 1.4 7.8-1.7 10.4s-7.6 2.4-10.5-.3l-112-104c-1.6-1.5-2.5-3.5-2.6-5.7s.8-4.3 2.3-5.8l75.4-75.4-54.8-85.2C167.4 35.4 150.5 32 133.1 32 59.6 32 0 91.6 0 165.1z"/><path d="M296.4 60.6C286.9 68.1 278.3 77 271 87.1l-15 20.7-15-20.7C226.1 66.5 205.9 51 183 41.7l54.8 85.2-75.4 75.4c-1.5 1.5-2.4 3.6-2.3 5.8s1 4.2 2.6 5.7l112 104c2.9 2.7 7.4 2.9 10.5 .3s3.8-7 1.7-10.4l-60.4-98.1 90.7-75.6c2.6-2.1 3.5-5.7 2.4-8.8L296.4 60.6z"/></svg>
            <span class="text-sm font-medium">This case has been declined.</span>
        </div>
    {:else if c.status === 'needs_revision'}
        {@const revisionEvent = [...workflowEvents].reverse().find(e => e.type === 'needs_revision')}
        <div class="rounded-lg border border-violet-500/30 bg-violet-950/40 px-5 py-4 text-violet-300">
            <div class="flex items-center gap-3">
                <svg class="size-5 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path opacity=".4" d="M0 160c0-53 43-96 96-96l96 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-96 0c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l256 0c17.7 0 32-14.3 32-32l0-96c0-17.7 14.3-32 32-32s32 14.3 32 32l0 96c0 53-43 96-96 96L96 512c-53 0-96-43-96-96L0 160z"/><path d="M392.4 21.7L368 46.1 465.9 144 490.3 119.6c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0zM334.1 80L172.4 241.7c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L432 177.9 334.1 80z"/></svg>
                <span class="text-sm font-medium">This case has been returned to its author for revisions.</span>
            </div>
            {#if revisionEvent?.detail}
                <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized markdown -->
                <div class="mt-2 ml-8 text-sm text-violet-400/80 prose prose-invert prose-sm max-w-none">{@html revisionEvent.detail}</div>
            {/if}
        </div>
    {/if}

    <!-- History -->
    <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
        {#if c.institution || c.authors_formatted}
            <div class="text-sm text-gray-500 mb-4">
                {#if c.institution}<p>Contributed by <span class="font-semibold text-gray-300">{c.institution}</span></p>{/if}
                {#if c.authors_formatted}<p>{c.authors_formatted}</p>{/if}
            </div>
        {/if}
        <h2 class="font-semibold text-gray-200 mb-3">History</h2>
        <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
        <div class="prose prose-invert prose-base max-w-none text-gray-200 text-pretty leading-relaxed">{@html c.history}</div>

        {#if lightboxImages.length > 0}
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                {#each c.media_urls as media, i (i)}
                    <figure>
                        <button type="button" onclick={(e) => openLightbox(i, e)} class="w-full aspect-[4/3] bg-gray-800 border-2 border-gray-700 rounded-lg cursor-zoom-in flex items-center justify-center overflow-hidden hover:bg-gray-800 transition-colors">
                            <img src={media.url} alt={media.caption || 'Case image'} class="max-w-full max-h-full object-contain" />
                        </button>
                        {#if media.caption}
                            <figcaption class="text-sm text-gray-500 mt-1">{media.caption}</figcaption>
                        {/if}
                    </figure>
                {/each}
            </div>
        {/if}
    </div>

    <!-- Questions -->
    {#each c.questions ?? [] as q (q.id)}
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
            <h2 class="font-semibold text-gray-200 mb-4 text-lg prose prose-invert prose-sm max-w-none">{@html q.body}</h2>
            {#if q.type === 'multiple_choice' && q.options?.length}
                <ul class="mt-3 space-y-1">
                    {#each q.options as opt (opt.body)}
                        <li class="text-sm {opt.is_correct ? 'text-green-400 font-medium' : 'text-gray-500'}">
                            {opt.body} {opt.is_correct ? '✓' : ''}
                        </li>
                    {/each}
                </ul>
            {/if}

            {#if q.type === 'free_text' && q.correct_answer}
                <div class="mt-4 border-t border-gray-700 pt-4">
                    <h3 class="font-semibold text-gray-200 mb-2">Correct Answer</h3>
                    <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                    <div class="prose prose-invert prose-sm max-w-none text-gray-300">{@html q.correct_answer}</div>
                </div>
            {/if}

            {#if answerStats.length > 0}
                <div class="mt-4 border-t border-gray-700 pt-4">
                    <button type="button" onclick={() => statsOpen = !statsOpen} class="flex items-center gap-2 w-full text-left">
                        <svg class="size-3.5 transition-transform {statsOpen ? 'rotate-90' : ''}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor"><path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>
                        <h3 class="font-semibold text-gray-200">Answer Statistics</h3>
                        {#if selectedStat}
                            <div class="ml-auto flex items-center gap-1">
                                <span class="text-sm font-bold text-gray-300 tabular-nums">{selectedStat.total}</span>
                                <span class="text-sm text-gray-400 mr-2">answers</span>
                                <svg class="size-12" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#374151" stroke-width="2.5" />
                                    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#34d399" stroke-width="2.5" stroke-dasharray="{selectedStat.percent_correct} {100 - selectedStat.percent_correct}" stroke-dashoffset="25" stroke-linecap="round" />
                                    <text x="18" y="20.5" text-anchor="middle" class="fill-gray-200 text-[0.65rem] font-bold">{selectedStat.percent_correct}%</text>
                                </svg>
                                <span class="text-sm text-gray-400">correct</span>
                            </div>
                        {/if}
                    </button>

                    {#if statsOpen}
                        <div transition:slide={{ duration: 200 }} class="mt-3">
                            {#if answerStats.length > 1}
                                <div class="flex flex-wrap gap-2 mb-4">
                                    {#each answerStats as stat (stat.schedule_id)}
                                        <button
                                            type="button"
                                            onclick={() => selectedStatScheduleId = stat.schedule_id}
                                            class="text-sm px-3 py-1 rounded-full transition-colors {selectedStat?.schedule_id === stat.schedule_id ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}"
                                        >
                                            {stat.date_formatted}
                                        </button>
                                    {/each}
                                </div>
                            {/if}

                            {#if selectedStat}
                                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                    <StatCard value={selectedStat.total} color="cyan" nablaClass="nabla-cyan" compact>
                                        {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 64L0 400c0 44.2 35.8 80 80 80l400 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L80 416c-8.8 0-16-7.2-16-16L64 64c0-17.7-14.3-32-32-32S0 46.3 0 64z"/><path fill="currentColor" d="M512 96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 224c0 17.7 14.3 32 32 32s32-14.3 32-32l0-224zM288 160c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 160c0 17.7 14.3 32 32 32s32-14.3 32-32l0-160zM176 256c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 64c0 17.7 14.3 32 32 32s32-14.3 32-32l0-64zm192-64c-17.7 0-32 14.3-32 32l0 96c0 17.7 14.3 32 32 32s32-14.3 32-32l0-96c0-17.7-14.3-32-32-32z"/></svg>{/snippet}
                                        {#snippet label()}Total{/snippet}
                                    </StatCard>
                                    <StatCard value={selectedStat.correct} color="emerald" nablaClass="nabla-emerald" compact>
                                        {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zm135.1 7.1c9.4-9.4 24.6-9.4 33.9 0L221.1 315.2 340.5 151c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8s-13.9-2-18.8-7l-72-72c-9.4-9.4-9.4-24.6 0-33.9z"/><path fill="currentColor" d="M340.5 151c7.8-10.7 22.8-13.1 33.5-5.3s13.1 22.8 5.3 33.5L243.4 366.1c-4.1 5.7-10.5 9.3-17.5 9.8s-13.9-2-18.8-7l-72-72c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L221.1 315.2 340.5 151z"/></svg>{/snippet}
                                        {#snippet label()}Correct{/snippet}
                                    </StatCard>
                                    <StatCard value={selectedStat.incorrect} color="rose" nablaClass="nabla-rose" compact>
                                        {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zm167-89c9.4-9.4 24.6-9.4 33.9 0l55 55 55-55c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-55 55 55 55c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-55-55-55 55c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l55-55-55-55c-9.4-9.4-9.4-24.6 0-33.9z"/><path fill="currentColor" d="M201 167c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l55 55-55 55c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l55-55 55 55c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-55-55 55-55c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-55 55-55-55z"/></svg>{/snippet}
                                        {#snippet label()}Incorrect{/snippet}
                                    </StatCard>
                                    <StatCard value={selectedStat.pending} color="gray" nablaClass="nabla-gray" compact>
                                        {#snippet icon()}<svg class="size-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M0 256a256 256 0 1 0 512 0 256 256 0 1 0 -512 0zM232.1 117.5c.1-.8 .2-1.6 .4-2.4 .3-1.6 .8-3.1 1.4-4.5 1.2-2.9 3-5.5 5.1-7.6 4.3-4.3 10.3-7 17-7 13.3 0 24 10.7 24 24l0 123.2c28.4 19 56.9 37.9 85.3 56.9 11 7.4 14 22.2 6.7 33.3s-22.3 14-33.3 6.7c-32-21.3-64-42.7-96-64-3.3-2.2-6-5.2-7.8-8.6-.9-1.7-1.6-3.5-2.1-5.4-.2-1-.4-1.9-.5-2.9-.1-.5-.1-1-.1-1.5s0-.9 0-1.5c0-45.3 0-90.7 0-136.3 0-.6 0-1.4 .1-2.2z"/><path fill="currentColor" d="M256 96c-13.3 0-24 10.7-24 24l0 136c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2 280 120c0-13.3-10.7-24-24-24z"/></svg>{/snippet}
                                        {#snippet label()}Pending{/snippet}
                                    </StatCard>
                                </div>

                                {#if selectedTimeSeries.length > 0}
                                    <div class="h-64">
                                        <canvas bind:this={chartCanvas}></canvas>
                                    </div>
                                {/if}
                            {/if}
                        </div>
                    {/if}
                </div>
            {/if}

            {#if aiAnswers.length > 0}
                <div class="mt-4 border-t border-gray-700 pt-4">
                    <button type="button" onclick={() => aiOpen = !aiOpen} class="flex items-center gap-2 w-full text-left">
                        <svg class="size-3.5 transition-transform {aiOpen ? 'rotate-90' : ''}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" fill="currentColor"><path d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"/></svg>
                        <h3 class="font-semibold text-gray-200">AI Agent Answers</h3>
                        <div class="ml-auto flex items-center gap-1">
                            <span class="text-sm font-bold text-gray-300 tabular-nums">{aiAnswers.length}</span>
                            <span class="text-sm text-gray-400 mr-2">answers</span>
                            {#if aiScored > 0}
                                <svg class="size-12" viewBox="0 0 36 36">
                                    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#374151" stroke-width="2.5" />
                                    <circle cx="18" cy="18" r="15.915" fill="none" stroke="#34d399" stroke-width="2.5" stroke-dasharray="{aiPercent} {100 - aiPercent}" stroke-dashoffset="25" stroke-linecap="round" />
                                    <text x="18" y="20.5" text-anchor="middle" class="fill-gray-200 text-[0.65rem] font-bold">{aiPercent}%</text>
                                </svg>
                                <span class="text-sm text-gray-400">correct</span>
                            {/if}
                        </div>
                    </button>

                    {#if aiOpen}
                        <div transition:slide={{ duration: 200 }} class="mt-3 overflow-x-auto">
                            <table class="w-full text-sm">
                                <thead>
                                    <tr class="border-b border-gray-700">
                                        <th class="text-left py-2 px-2 text-gray-500 font-medium">Model</th>
                                        <th class="text-left py-2 px-2 text-gray-500 font-medium">Answer</th>
                                        <th class="text-center py-2 px-2 text-gray-500 font-medium">Correct?</th>
                                        <th class="text-left py-2 px-2 text-gray-500 font-medium">Reasoning</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each aiAnswers as ai, i (i)}
                                        <tr class="border-b border-gray-800">
                                            <td class="py-2 px-2 text-gray-300 whitespace-nowrap">
                                                <span class="inline-flex items-center gap-1.5">
                                                    {#if ai.provider === 'anthropic'}
                                                        <svg class="size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M100.4 340.5l100.7-56.5 1.7-4.9-1.7-2.7-4.9 0-16.8-1-57.5-1.6-49.9-2.1-48.3-2.6-12.2-2.6-11.4-15 1.2-7.5 10.2-6.9 14.7 1.3c18.9 1.3 45.9 3.1 81 5.6l35.2 2.1 52.2 5.4 8.3 0 1.2-3.4-2.8-2.1-2.2-2.1-50.3-34.1-54.4-36-28.5-20.7-15.4-10.5-7.8-9.8-3.4-21.5 14-15.4 18.8 1.3 4.8 1.3 19 14.7 40.7 31.5 53.1 39.1 7.8 6.5 3.1-2.2 .4-1.6-3.5-5.8-28.9-52.2-30.8-53.1-13.7-22-3.6-13.2c-1.3-5.4-2.2-10-2.2-15.5l15.9-21.6 8.8-2.8 21.2 2.8 8.9 7.8 13.2 30.2 21.4 47.5 33.2 64.6 9.7 19.2 5.2 17.8 1.9 5.4 3.4 0 0-3.1 2.7-36.4 5-44.7 4.9-57.5 1.7-16.2 8-19.4 15.9-10.5 12.4 5.9 10.2 14.7-1.4 9.5-6.1 39.5-11.9 61.9-7.8 41.5 4.5 0 5.2-5.2 21-27.8 35.2-44.1 15.5-17.5 18.1-19.3 11.6-9.2 22 0 16.2 24.1-7.3 24.9-22.7 28.7-18.8 24.4-27 36.3-16.8 29 1.6 2.3 4-.4 60.9-13 32.9-5.9 39.3-6.7 17.8 8.3 1.9 8.4-7 17.2-42 10.4-49.2 9.8-73.3 17.3-.9 .7 1 1.3 33 3.1 14.1 .8 34.6 0 64.4 4.8 16.8 11.1 10.1 13.6-1.7 10.4-25.9 13.2c-15.5-3.7-54.4-12.9-116.6-27.7l-28-7-3.9 0 0 2.3 23.3 22.8 42.7 38.6 53.5 49.8 2.7 12.3-6.9 9.7-7.3-1-47-35.4-18.1-15.9-41.1-34.6-2.7 0 0 3.6 9.5 13.9 50 75.2 2.6 23-3.6 7.5-13 4.5-14.2-2.6-29.3-41.1-30.2-46.3-24.4-41.5-3 1.7-14.4 154.8-6.7 7.9-15.5 5.9-13-9.8-6.9-15.9 6.9-31.5 8.3-41.1 6.7-32.7 6.1-40.6 3.6-13.5-.2-.9-3 .4-30.6 42-46.5 62.9-36.8 39.4-8.8 3.5-15.3-7.9 1.4-14.1 8.5-12.6 50.9-64.8 30.7-40.2 19.8-23.2-.1-3.4-1.2 0-135.3 87.8-24.1 3.1-10.4-9.7 1.3-15.9 4.9-5.2 40.7-28-.1 .1 0 .1z"/></svg>
                                                    {:else if ai.provider === 'openai'}
                                                        <svg class="size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M196.4 185.8l0-48.6c0-4.1 1.5-7.2 5.1-9.2l97.8-56.3c13.3-7.7 29.2-11.3 45.6-11.3 61.4 0 100.4 47.6 100.4 98.3 0 3.6 0 7.7-.5 11.8L343.3 111.1c-6.1-3.6-12.3-3.6-18.4 0L196.4 185.8zM424.7 375.2l0-116.2c0-7.2-3.1-12.3-9.2-15.9L287 168.4 329 144.3c3.6-2 6.7-2 10.2 0L437 200.7c28.2 16.4 47.1 51.2 47.1 85 0 38.9-23 74.8-59.4 89.6l0 0zM166.2 272.8l-42-24.6c-3.6-2-5.1-5.1-5.1-9.2l0-112.6c0-54.8 42-96.3 98.8-96.3 21.5 0 41.5 7.2 58.4 20L175.4 108.5c-6.1 3.6-9.2 8.7-9.2 15.9l0 148.5 0 0zm90.4 52.2l-60.2-33.8 0-71.7 60.2-33.8 60.2 33.8 0 71.7-60.2 33.8zm38.7 155.7c-21.5 0-41.5-7.2-58.4-20l100.9-58.4c6.1-3.6 9.2-8.7 9.2-15.9l0-148.5 42.5 24.6c3.6 2 5.1 5.1 5.1 9.2l0 112.6c0 54.8-42.5 96.3-99.3 96.3l0 0zM173.8 366.5L76.1 310.2c-28.2-16.4-47.1-51.2-47.1-85 0-39.4 23.6-74.8 59.9-89.6l0 116.7c0 7.2 3.1 12.3 9.2 15.9l128 74.2-42 24.1c-3.6 2-6.7 2-10.2 0zm-5.6 84c-57.9 0-100.4-43.5-100.4-97.3 0-4.1 .5-8.2 1-12.3l100.9 58.4c6.1 3.6 12.3 3.6 18.4 0l128.5-74.2 0 48.6c0 4.1-1.5 7.2-5.1 9.2l-97.8 56.3c-13.3 7.7-29.2 11.3-45.6 11.3l0 0zm127 60.9c62 0 113.7-44 125.4-102.4 57.3-14.9 94.2-68.6 94.2-123.4 0-35.8-15.4-70.7-43-95.7 2.6-10.8 4.1-21.5 4.1-32.3 0-73.2-59.4-128-128-128-13.8 0-27.1 2-40.4 6.7-23-22.5-54.8-36.9-89.6-36.9-62 0-113.7 44-125.4 102.4-57.3 14.8-94.2 68.6-94.2 123.4 0 35.8 15.4 70.7 43 95.7-2.6 10.8-4.1 21.5-4.1 32.3 0 73.2 59.4 128 128 128 13.8 0 27.1-2 40.4-6.7 23 22.5 54.8 36.9 89.6 36.9z"/></svg>
                                                    {:else if ai.provider === 'gemini'}
                                                        <svg class="size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M500 261.8C500 403.3 403.1 504 260 504 122.8 504 12 393.2 12 256S122.8 8 260 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9c-88.3-85.2-252.5-21.2-252.5 118.2 0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9l-140.8 0 0-85.3 236.1 0c2.3 12.7 3.9 24.9 3.9 41.4z"/></svg>
                                                    {/if}
                                                    {ai.model_name}
                                                </span>
                                            </td>
                                            <td class="py-2 px-2 text-gray-300">{ai.selected_option_body ?? ai.response_text ?? '—'}</td>
                                            <td class="py-2 px-2 text-center">
                                                {#if ai.is_correct === true}
                                                    <span class="text-emerald-400">&#10003;</span>
                                                {:else if ai.is_correct === false}
                                                    <span class="text-rose-400">&#10007;</span>
                                                {:else}
                                                    <span class="text-gray-500">—</span>
                                                {/if}
                                            </td>
                                            <td class="py-2 px-2">
                                                {#if ai.reasoning}
                                                    <button
                                                        type="button"
                                                        onclick={() => toggleAiRow(i)}
                                                        class="text-cyan-400 hover:underline text-xs"
                                                    >
                                                        {expandedAiRows.has(i) ? 'Hide' : 'Show'}
                                                    </button>
                                                {:else}
                                                    <span class="text-gray-500">—</span>
                                                {/if}
                                            </td>
                                        </tr>
                                        {#if expandedAiRows.has(i) && ai.reasoning}
                                            <tr>
                                                <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-parsed markdown -->
                                                <td colspan="4" class="px-2 py-3 text-gray-400 text-xs bg-gray-800/50 prose prose-invert prose-xs max-w-none">{@html ai.reasoning}</td>
                                            </tr>
                                        {/if}
                                    {/each}
                                </tbody>
                            </table>
                        </div>
                    {/if}
                </div>
            {/if}
        </div>
    {/each}

    <!-- Discussion / Differential / Additional Images / References -->
    {#if c.discussion || c.differential || additionalLightboxImages.length > 0 || (c.references && c.references.length > 0)}
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-6">
            {#if c.discussion}
                <section>
                    <h2 class="font-semibold text-gray-200 mb-3">Discussion</h2>
                    <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                    <div class="prose prose-invert prose-sm max-w-none text-gray-300">{@html c.discussion}</div>
                </section>
            {/if}

            {#if c.differential}
                <section>
                    <h2 class="font-semibold text-gray-200 mb-3">Differential Diagnosis</h2>
                    <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-sanitized content -->
                    <div class="prose prose-invert prose-sm max-w-none text-gray-300">{@html c.differential}</div>
                </section>
            {/if}

            {#if additionalLightboxImages.length > 0}
                <section>
                    <h2 class="font-semibold text-gray-200 mb-3">Additional Images</h2>
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {#each c.additional_media_urls as media, i (i)}
                            <figure>
                                <button type="button" onclick={(e) => openAdditionalLightbox(i, e)} class="w-full aspect-[4/3] bg-gray-800 border-2 border-gray-700 rounded-lg cursor-zoom-in flex items-center justify-center overflow-hidden hover:bg-gray-800 transition-colors">
                                    <img src={media.url} alt={media.caption || 'Additional image'} class="max-w-full max-h-full object-contain" />
                                </button>
                                {#if media.caption}
                                    <figcaption class="text-sm text-gray-500 mt-1">{media.caption}</figcaption>
                                {/if}
                            </figure>
                        {/each}
                    </div>
                </section>
            {/if}

            {#if c.references && c.references.length > 0}
                <section>
                    <h2 class="font-semibold text-gray-200 mb-3">References</h2>
                    <ol class="space-y-2 text-sm text-gray-300 prose prose-invert prose-sm max-w-none">
                        {#each c.references as ref, i (i)}
                            <li class="flex gap-3 items-start">
                                <span class="shrink-0 size-6 rounded-full bg-gray-700 text-gray-400 text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</span>
                                <!-- eslint-disable-next-line svelte/no-at-html-tags -- server-linkified content -->
                                <span>{@html ref}</span>
                            </li>
                        {/each}
                    </ol>
                </section>
            {/if}
        </div>
    {/if}

    <!-- Review Actions (floating toolbar rendered below) -->

    <!-- Activity Feed -->
    {#if activity.length > 0}
        <div>
            <h2 class="font-semibold mb-4">Activity</h2>
            <ActivityFeed {activity} />
        </div>
    {/if}
</div>

<!-- Workflow Timeline Sidebar -->
{#if workflowEvents.length > 0}
    <aside class="hidden lg:block w-56 shrink-0">
        <div>
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Workflow</h3>
            <div class="relative pl-8">
                <div class="absolute left-[11px] top-1 bottom-1 w-px bg-gray-800"></div>
                {#each workflowEvents as event, i (i)}
                    {@const config = getWorkflowConfig(event.type)}
                    <div class="relative pb-5 last:pb-0">
                        <div class="absolute -left-8 top-0 flex items-center justify-center size-[24px] rounded-full {config.dot} ring-2 ring-gray-950">
                            <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                            {@html config.icon}
                        </div>
                        <div class="min-w-0">
                            <p class="text-xs font-medium text-gray-300 leading-snug">{event.label}</p>
                            <div class="text-[10px] text-gray-600 mt-0.5">
                                {#if event.actor}<span class="text-gray-500">{event.actor}</span> &middot; {/if}<time class="tabular-nums" title={event.timestamp}>{timeAgo(event.timestamp)}</time>
                            </div>
                        </div>
                    </div>
                {/each}
            </div>
        </div>
    </aside>
{/if}
</div>

<!-- Accept Confirmation Modal -->
<ConfirmModal
    bind:open={approveModalOpen}
    title="Accept Case"
    message="This case will be approved and available for scheduling."
    confirmLabel="Accept"
    cancelLabel="Cancel"
    variant="success"
    panelClass="shadow-[0_0_40px_rgba(34,197,94,0.3)] border border-green-500/30"
    onconfirm={approve}
    oncancel={() => approveModalOpen = false}
/>

<!-- Decline Confirmation Modal -->
<ConfirmModal
    bind:open={declineModalOpen}
    title="Decline Case"
    message="This case will be permanently declined. This action cannot be undone."
    confirmLabel="Decline Case"
    cancelLabel="Cancel"
    variant="danger"
    panelClass="danger-glow"
    onconfirm={decline}
    oncancel={() => declineModalOpen = false}
/>

<!-- Request Revision Modal -->
<Modal bind:open={revisionModalOpen} title="Request Revision" panelClass="shadow-[0_0_30px_rgba(139,92,246,0.25)] border border-violet-500/30">
    <p class="text-sm text-gray-400 mb-4">The case will be returned to the submitter for editing. Describe what needs to be changed.</p>
    <textarea
        bind:value={revisionFeedback}
        rows="4"
        placeholder="Describe the changes needed..."
        class="w-full rounded-md bg-gray-800 border border-gray-700 text-gray-200 text-sm px-3 py-2 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
    ></textarea>
    <div class="mt-4 flex justify-end gap-3">
        <button
            onclick={() => revisionModalOpen = false}
            class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-gray-700"
        >
            Cancel
        </button>
        <button
            onclick={requestRevision}
            disabled={!revisionFeedback.trim()}
            class="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            Request Revision
        </button>
    </div>
</Modal>

{#if c.status === 'submitted'}
    <div class="fixed bottom-0 inset-x-0 z-40 border-t border-gray-700 bg-gray-900/95 backdrop-blur-sm shadow-[0_-4px_16px_rgba(0,0,0,0.3)]">
        <div class="max-w-3xl mx-auto flex items-center justify-between px-6 py-3">
            <span class="text-sm font-semibold text-gray-300">Review Case</span>
            <div class="flex gap-3">
                <button onclick={() => approveModalOpen = true} class="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition-colors">
                    Accept
                </button>
                <button onclick={() => { revisionFeedback = ''; revisionModalOpen = true; }} class="bg-violet-600 text-white px-4 py-2 rounded text-sm hover:bg-violet-700 transition-colors">
                    Request Revision
                </button>
                <button onclick={() => declineModalOpen = true} class="bg-rose-600 text-white px-4 py-2 rounded text-sm hover:bg-rose-700 transition-colors">
                    Decline
                </button>
            </div>
        </div>
    </div>
{/if}

<MediaLightbox
    bind:this={lightboxRef}
    images={lightboxImages}
    bind:index={lightboxIndex}
    bind:open={lightboxOpen}
    onclose={() => lightboxOpen = false}
/>

<MediaLightbox
    bind:this={additionalLightboxRef}
    images={additionalLightboxImages}
    bind:index={additionalLightboxIndex}
    bind:open={additionalLightboxOpen}
    onclose={() => additionalLightboxOpen = false}
/>
